/**
 * OAuth flow utilities for EagleView integration.
 *
 * Contains helper functions for the Authorization Code + PKCE flow
 * that are extracted for independent testability.
 */

import { EagleViewOAuthConfig } from './eagleviewConfig';

/** The required OAuth scopes for EagleView integration */
export const EAGLEVIEW_SCOPES =
  'openid offline_access Order Download_Report AdjustOrder getReportDetail';

/** SessionStorage key for the OAuth state parameter */
export const SESSION_KEY_STATE = 'eagleview_oauth_state';

/** SessionStorage key for the PKCE code verifier */
export const SESSION_KEY_CODE_VERIFIER = 'eagleview_code_verifier';

/**
 * Generates a cryptographically random state string for OAuth CSRF protection.
 * Uses crypto.getRandomValues and base64url encoding.
 *
 * @returns A random URL-safe string suitable for use as an OAuth state parameter.
 */
export function generateRandomState(): string {
  const randomBytes = new Uint8Array(32);
  crypto.getRandomValues(randomBytes);

  let base64 = '';
  for (let i = 0; i < randomBytes.length; i++) {
    base64 += String.fromCharCode(randomBytes[i]);
  }
  base64 = btoa(base64);

  // Convert to base64url: replace + with -, / with _, remove trailing =
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Constructs the EagleView OAuth authorization URL with all required query parameters.
 *
 * @param config - The EagleView OAuth configuration (client_id, authEndpoint, redirectUri)
 * @param state - The random state string for CSRF protection
 * @param codeChallenge - The PKCE code challenge (base64url-encoded SHA-256 of verifier)
 * @returns The fully constructed authorization URL string
 */
export function buildAuthorizationUrl(
  config: EagleViewOAuthConfig,
  state: string,
  codeChallenge: string
): string {
  const params = new URLSearchParams({
    client_id: config.clientId,
    response_type: 'code',
    scope: EAGLEVIEW_SCOPES,
    redirect_uri: config.redirectUri,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  });

  return `${config.authEndpoint}?${params.toString()}`;
}

/**
 * Validates that the received OAuth state matches the stored state.
 * This prevents CSRF attacks by ensuring the callback originated from
 * a flow initiated by this application.
 *
 * @param receivedState - The state parameter received from the OAuth callback URL
 * @param storedState - The state value stored in sessionStorage (may be null if missing)
 * @returns true if states match, false otherwise
 */
export function validateOAuthState(
  receivedState: string,
  storedState: string | null
): boolean {
  if (!storedState) {
    return false;
  }
  return receivedState === storedState;
}

/**
 * Constructs the URL-encoded request body for the OAuth token exchange.
 *
 * @param code - The authorization code received from the OAuth callback
 * @param redirectUri - The redirect URI (must match the one used in the authorization request)
 * @param clientId - The OAuth client ID
 * @param clientSecret - The OAuth client secret
 * @param codeVerifier - The PKCE code verifier stored during authorization initiation
 * @returns A URLSearchParams-encoded string suitable for a POST body with Content-Type application/x-www-form-urlencoded
 */
export function buildTokenExchangeBody(
  code: string,
  redirectUri: string,
  clientId: string,
  clientSecret: string,
  codeVerifier: string
): string {
  const params = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: redirectUri,
    client_id: clientId,
    client_secret: clientSecret,
    code_verifier: codeVerifier,
  });

  return params.toString();
}

/**
 * Removes all EagleView OAuth transient data from sessionStorage.
 * Called after callback processing completes (success or failure)
 * to ensure no sensitive data persists in the browser.
 */
export function cleanupSessionStorage(): void {
  sessionStorage.removeItem(SESSION_KEY_STATE);
  sessionStorage.removeItem(SESSION_KEY_CODE_VERIFIER);
}
